"use client";

import { useT } from "@/lib/i18n/context";
import type { WShopPromotionalProductsContent } from "@/lib/api/cms-window-content";
import { ProductCard } from "@/components/ui/ProductCard/ProductCard";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import Link from "next/link";
import { wShopPromotionalProductsTranslations } from "./WShopPromotionalProducts.i18n";
import styles from "./WShopPromotionalProducts.module.css";

/**
 * WShopPromotionalProducts - okno "produkty w promocji".
 *
 * Grid kafelkow produktow (reuse `<ProductCard />` z ui/), naglowek sekcji z
 * tytulem instancji okna + opcjonalny przycisk "zobacz wszystkie" gdy admin
 * ustawil `config[list_url]`. Fallback title z i18n gdy admin nie nazwal
 * instancji (`content.title` puste).
 *
 * Data source: `W_SHOP_PROMOTIONAL_PRODUCTS_CONTROLLER::getApiContent()`.
 */
export function WShopPromotionalProducts({
  content,
  title,
}: WShopPromotionalProductsProps) {
  const t = useT(wShopPromotionalProductsTranslations);

  if (!content) return null;

  const headingText = title || content.title || t.title;
  const showAllHref = content.href ? extractInternalHref(content.href) : null;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.heading}>{headingText}</h2>
        {content.href && (
          showAllHref !== null ? (
            <Link href={showAllHref} className={styles.showAllBtn}>
              {t.showAll}
            </Link>
          ) : (
            <a href={content.href} className={styles.showAllBtn}>
              {t.showAll}
            </a>
          )
        )}
      </div>

      {content.items.length === 0 ? (
        <div className={styles.empty}>{t.noProducts}</div>
      ) : (
        <ul className={styles.productList}>
          {content.items.map((item) => (
            <ProductCard
              key={item.id}
              item={item}
              currency={content.currency}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export type WShopPromotionalProductsProps = {
  content: WShopPromotionalProductsContent | null;
  title: string;
};
